import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  DEFAULT_NOTIFICATIONS_LIMIT,
  buildNotificationsStreamUrl,
  fetchNotificationsPage,
  fetchUnreadNotificationsCount,
  markAllNotificationsAsRead,
  markNotificationAsRead,
  NotificationAction,
  NotificationsPage,
  NotificationItem,
  sortNotificationsByCreatedAt,
} from "../services/notifications";
import { addToast } from "@heroui/react";
import { getCleanAccessToken } from "@/utils/auth-token";
import { describeErrorForLog } from "@/utils/sentry-error";
import { sentryLog } from "@/utils/sentry-utils";

const NOTIFICATION_EVENT_NAME = "notification";
const NOTIFICATION_POPUP_TIMEOUT_MS = 5000;
const SSE_INITIAL_RETRY_MS = 1000;
const SSE_MAX_RETRY_MS = 30_000;

export const NOTIFICATIONS_QUERY_KEY = ["notifications"] as const;
export const NOTIFICATIONS_UNREAD_COUNT_QUERY_KEY = [
  "notifications",
  "unread-count",
] as const;

type MarkAsReadContext = {
  previousNotifications: InfiniteData<NotificationsPage> | undefined;
};

export function useNotifications({
  enabled = true,
}: {
  enabled?: boolean;
} = {}) {
  const queryClient = useQueryClient();
  const popupTimeoutRef = useRef<null | number>(null);
  // const [incomingNotificationPopup, setIncomingNotificationPopup] =
  //   useState<NotificationItem | null>(null);

  function showIncomingNotificationPopup(notification: NotificationItem) {
    addToast({
      title: notification.title,
      description: notification.message,
      color: "default",
      timeout: NOTIFICATION_POPUP_TIMEOUT_MS,
      severity: "default",
    });

    // setIncomingNotificationPopup(notification);

    // if (popupTimeoutRef.current != null) {
    //   window.clearTimeout(popupTimeoutRef.current);
    // }

    // popupTimeoutRef.current = window.setTimeout(() => {
    //   setIncomingNotificationPopup((currentNotification) => {
    //     if (currentNotification?.id !== notification.id) {
    //       return currentNotification;
    //     }

    //     return null;
    //   });
    //   popupTimeoutRef.current = null;
    // }, NOTIFICATION_POPUP_TIMEOUT_MS);
  }

  // function dismissIncomingNotificationPopup() {
  //   setIncomingNotificationPopup(null);

  //   if (popupTimeoutRef.current != null) {
  //     window.clearTimeout(popupTimeoutRef.current);
  //     popupTimeoutRef.current = null;
  //   }
  // }

  const notificationsQuery = useInfiniteQuery({
    queryKey: NOTIFICATIONS_QUERY_KEY,
    queryFn: ({ pageParam }) =>
      fetchNotificationsPage({
        limit: DEFAULT_NOTIFICATIONS_LIMIT,
        cursor: (pageParam as NotificationsPage["nextCursor"]) ?? null,
      }),
    initialPageParam: null as NotificationsPage["nextCursor"],
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled,
    refetchInterval: 60_000,
  });

  const notificationListenerRef = useRef<EventListener | null>(null);

  const handleNotificationEvent = useCallback(
    (event: Event) => {
      const data = parseNotificationEventData(event as MessageEvent<string>);

      if (!data) {
        return;
      }

      const currentNotifications = flattenNotifications(
        queryClient.getQueryData<InfiniteData<NotificationsPage>>(
          NOTIFICATIONS_QUERY_KEY,
        ),
      );
      const isExistingNotification = currentNotifications.some(
        (notification) => notification.id === data.id,
      );

      if (!isExistingNotification) {
        showIncomingNotificationPopup(data);
      }

      queryClient.setQueryData<InfiniteData<NotificationsPage>>(
        NOTIFICATIONS_QUERY_KEY,
        (notificationsInCache) =>
          prependNotificationInInfiniteData(notificationsInCache, data),
      );

      queryClient.invalidateQueries({
        queryKey: NOTIFICATIONS_UNREAD_COUNT_QUERY_KEY,
      });
    },
    [queryClient],
  );

  useEffect(() => {
    notificationListenerRef.current = handleNotificationEvent;
  }, [handleNotificationEvent]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let stream: EventSource | null = null;
    let retryTimeout: number | null = null;
    let retryDelay = SSE_INITIAL_RETRY_MS;
    let disposed = false;

    function retryLater() {
      // Auth token may not be in localStorage yet on first mount, and the
      // ticket request can fail transiently. Retry with the same backoff used
      // for connection errors so we don't stay dead until a remount.
      retryTimeout = window.setTimeout(() => {
        retryTimeout = null;
        connect();
      }, retryDelay);
      retryDelay = Math.min(retryDelay * 2, SSE_MAX_RETRY_MS);
    }

    async function connect() {
      if (disposed) {
        return;
      }

      if (!getCleanAccessToken()) {
        retryLater();
        return;
      }

      const streamUrl = await (async () => {
        try {
          return await buildNotificationsStreamUrl();
        } catch (error) {
          sentryLog.warn(
            "Notification stream ticket request failed",
            describeErrorForLog(error),
          );
          return null;
        }
      })();

      // The await above spans a possible unmount.
      if (disposed) {
        return;
      }

      if (!streamUrl) {
        retryLater();
        return;
      }

      // Without `withCredentials` the browser omits the navigation-ticket
      // cookie on the cross-origin stream request. `connect` runs un-awaited,
      // so a constructor throw here would escape as an unhandled rejection and
      // leave the stream dead until remount.
      try {
        stream = new EventSource(streamUrl, { withCredentials: true });
      } catch (error) {
        sentryLog.warn(
          "Notification stream could not be opened",
          describeErrorForLog(error),
        );
        retryLater();
        return;
      }

      stream.addEventListener(NOTIFICATION_EVENT_NAME, (event) => {
        notificationListenerRef.current?.(event);
      });

      stream.addEventListener("open", () => {
        retryDelay = SSE_INITIAL_RETRY_MS;
      });

      stream.addEventListener("error", () => {
        if (disposed) {
          return;
        }

        stream?.close();
        stream = null;

        retryTimeout = window.setTimeout(() => {
          retryTimeout = null;
          connect();
        }, retryDelay);

        retryDelay = Math.min(retryDelay * 2, SSE_MAX_RETRY_MS);
      });
    }

    connect();

    return () => {
      disposed = true;

      if (retryTimeout != null) {
        window.clearTimeout(retryTimeout);
      }

      if (stream) {
        stream.close();
      }
    };
  }, [enabled]);

  useEffect(() => {
    return () => {
      if (popupTimeoutRef.current != null) {
        window.clearTimeout(popupTimeoutRef.current);
      }
    };
  }, []);

  const markAsReadMutation = useMutation<
    unknown,
    Error,
    number,
    MarkAsReadContext
  >({
    mutationFn: markNotificationAsRead,
    onMutate: async (notificationId) => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });

      const previousNotifications = queryClient.getQueryData<
        InfiniteData<NotificationsPage>
      >(NOTIFICATIONS_QUERY_KEY);

      queryClient.setQueryData<InfiniteData<NotificationsPage>>(
        NOTIFICATIONS_QUERY_KEY,
        (currentNotifications) =>
          markSingleNotificationAsReadInInfiniteData(
            currentNotifications,
            notificationId,
          ),
      );

      return {
        previousNotifications,
      };
    },
    onError: (_error, _notificationId, context) => {
      if (!context?.previousNotifications) {
        return;
      }

      queryClient.setQueryData(
        NOTIFICATIONS_QUERY_KEY,
        context.previousNotifications,
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: NOTIFICATIONS_UNREAD_COUNT_QUERY_KEY,
      });
    },
  });

  const markAllAsReadMutation = useMutation<
    unknown,
    Error,
    void,
    MarkAsReadContext
  >({
    mutationFn: markAllNotificationsAsRead,
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });

      const previousNotifications = queryClient.getQueryData<
        InfiniteData<NotificationsPage>
      >(NOTIFICATIONS_QUERY_KEY);

      queryClient.setQueryData<InfiniteData<NotificationsPage>>(
        NOTIFICATIONS_QUERY_KEY,
        (currentNotifications) =>
          markAllNotificationsAsReadInInfiniteData(currentNotifications),
      );

      return {
        previousNotifications,
      };
    },
    onError: (_error, _variables, context) => {
      if (!context?.previousNotifications) {
        return;
      }

      queryClient.setQueryData(
        NOTIFICATIONS_QUERY_KEY,
        context.previousNotifications,
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: NOTIFICATIONS_UNREAD_COUNT_QUERY_KEY,
      });
    },
  });

  const unreadCountQuery = useQuery({
    queryKey: NOTIFICATIONS_UNREAD_COUNT_QUERY_KEY,
    queryFn: fetchUnreadNotificationsCount,
    enabled,
    refetchInterval: 60_000,
  });

  const notifications = useMemo(
    () => flattenNotifications(notificationsQuery.data),
    [notificationsQuery.data],
  );

  const unreadCount = unreadCountQuery.data ?? 0;

  return {
    notifications,
    unreadCount,
    isLoading: notificationsQuery.isLoading,
    isFetching: notificationsQuery.isFetching,
    hasNextPage: notificationsQuery.hasNextPage,
    isFetchingNextPage: notificationsQuery.isFetchingNextPage,
    fetchNextPage: notificationsQuery.fetchNextPage,
    isMarkingAsRead:
      markAsReadMutation.isPending || markAllAsReadMutation.isPending,
    markAsRead: markAsReadMutation.mutate,
    markAsReadAsync: markAsReadMutation.mutateAsync,
    markAllAsRead: markAllAsReadMutation.mutate,
    markAllAsReadAsync: markAllAsReadMutation.mutateAsync,
    // incomingNotificationPopup,
    // dismissIncomingNotificationPopup,
  };
}

function flattenNotifications(
  data: InfiniteData<NotificationsPage> | undefined,
) {
  if (!data) {
    return [];
  }

  return data.pages.flatMap((page) => page.notifications);
}

function prependNotificationInInfiniteData(
  data: InfiniteData<NotificationsPage> | undefined,
  notification: NotificationItem,
) {
  if (!data) {
    return data;
  }

  const pagesWithoutNotification = data.pages.map((page) => ({
    ...page,
    notifications: page.notifications.filter(
      (item) => item.id !== notification.id,
    ),
  }));

  const firstPage = pagesWithoutNotification[0];
  if (!firstPage) {
    return data;
  }

  const updatedFirstPage: NotificationsPage = {
    ...firstPage,
    notifications: sortNotificationsByCreatedAt([
      notification,
      ...firstPage.notifications,
    ]),
  };

  return {
    ...data,
    pages: [updatedFirstPage, ...pagesWithoutNotification.slice(1)],
  };
}

function markSingleNotificationAsReadInInfiniteData(
  data: InfiniteData<NotificationsPage> | undefined,
  notificationId: number,
) {
  if (!data) {
    return data;
  }

  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      notifications: page.notifications.map((notification) => {
        if (notification.id !== notificationId) {
          return notification;
        }

        return {
          ...notification,
          isRead: true,
          readAt: new Date().toISOString(),
        };
      }),
    })),
  };
}

function markAllNotificationsAsReadInInfiniteData(
  data: InfiniteData<NotificationsPage> | undefined,
) {
  if (!data) {
    return data;
  }

  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      notifications: page.notifications.map((notification) => ({
        ...notification,
        isRead: true,
        readAt: notification.readAt ?? new Date().toISOString(),
      })),
    })),
  };
}

function parseNotificationEventData(event: MessageEvent<string>) {
  try {
    const data = JSON.parse(event.data) as Partial<NotificationItem>;

    if (!isNotificationEventPayload(data)) {
      return null;
    }

    const rawActions = Array.isArray(data.actions) ? data.actions : [];

    return {
      id: data.id,
      tenantId: data.tenantId,
      title: data.title,
      message: data.message,
      type: data.type,
      isRead: data.isRead,
      createdAt: data.createdAt,
      readAt: data.readAt ?? null,
      actions: rawActions.filter(isNotificationAction),
    } satisfies NotificationItem;
  } catch (_error) {
    return null;
  }
}

function isNotificationEventPayload(
  data: Partial<NotificationItem>,
): data is NotificationItem {
  if (typeof data.id !== "number") {
    return false;
  }

  if (typeof data.tenantId !== "number") {
    return false;
  }

  if (typeof data.title !== "string") {
    return false;
  }

  if (typeof data.message !== "string") {
    return false;
  }

  if (data.type !== "alert" && data.type !== "update") {
    return false;
  }

  if (typeof data.isRead !== "boolean") {
    return false;
  }

  if (typeof data.createdAt !== "string") {
    return false;
  }

  if (data.readAt != null && typeof data.readAt !== "string") {
    return false;
  }

  return true;
}

function isNotificationAction(value: unknown): value is NotificationAction {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const action = value as Record<string, unknown>;
  // Forward-compat: future action kinds fail this check and are dropped
  // from the actions list — the notification still renders, just without
  // the unsupported chip.
  if (action.kind !== "calendar_link") {
    return false;
  }
  if (typeof action.date !== "string" || typeof action.label !== "string") {
    return false;
  }

  if (action.startTime != null && typeof action.startTime !== "string") {
    return false;
  }

  if (
    action.appointmentId != null &&
    typeof action.appointmentId !== "number"
  ) {
    return false;
  }

  if (action.locationId != null && typeof action.locationId !== "number") {
    return false;
  }

  return true;
}

export function useUnreadNotificationsCount({
  enabled = true,
}: {
  enabled?: boolean;
} = {}) {
  const query = useQuery({
    queryKey: NOTIFICATIONS_UNREAD_COUNT_QUERY_KEY,
    queryFn: fetchUnreadNotificationsCount,
    enabled,
    refetchInterval: 60_000,
  });

  return query.data ?? 0;
}
